/**
 * [Servexa] Pure presentation/validation logic for the Super Admin
 * Integration panel (spec A9/A20).
 *
 * Kept separate from the page component so it can be unit-tested under the
 * repo's existing `node --test` harness (there is no DOM/component test
 * runner here, and adding one would be a parallel test architecture). The
 * page (`pages/platform/ServexaIntegrationPage.jsx`) is a thin shell over
 * these helpers + the shared query/mutation + shared UI components.
 *
 * Security invariants encoded here (spec A19):
 *   - The raw integration key exists ONLY as a transient form input. These
 *     helpers put it in a request BODY object and nowhere else — never a URL,
 *     query string, or storage key.
 *   - The panel VIEW returned by the backend carries only `apiKeyMasked`,
 *     never the raw key (enforced by `servexaIntegrationView` in lib/types.js,
 *     which strips undeclared fields).
 */

/** Connection lifecycle states, mirroring `ServexaIntegration.lastStatus`
 *  (spec A9.5). `ERROR` is the reserved "couldn't reach Servexa" state. */
export const SERVEXA_STATUSES = [
  'NOT_CONFIGURED',
  'KEY_GENERATED',
  'PENDING',
  'ACTIVE',
  'REJECTED',
  'REVOKED',
  'ERROR',
]

/** A key looks like `sup_<prefix>_<64 hex>` (spec A9.1). Opaque — only
 *  shape-checked so an obvious paste error is caught before it 401s. Mirrors
 *  the server-side `API_KEY_SHAPE` in servexa.integration.service.js. */
export const API_KEY_SHAPE = /^sup_[A-Za-z0-9-]{1,20}_[0-9a-fA-F]{64}$/

export function isValidApiKeyShape(raw) {
  return API_KEY_SHAPE.test(String(raw ?? '').trim())
}

/** Webhook signing secret shape `whsec_<64 hex>` (spec A13.4). Mirrors the
 *  server-side check in servexa.integration.service.js. */
export const WEBHOOK_SECRET_SHAPE = /^whsec_[0-9a-fA-F]{64}$/

export function isValidWebhookSecretShape(raw) {
  return WEBHOOK_SECRET_SHAPE.test(String(raw ?? '').trim())
}

/** Light client check; the backend `inspectBaseUrl` is authoritative and
 *  returns the real field error on save (spec A9.7/A23). */
export function looksLikeAbsoluteUrl(value) {
  return /^https?:\/\/.+/i.test(String(value ?? '').trim())
}

/**
 * Pre-fill value for the Base URL input: the effective value the backend
 * reports (DB value if saved, else the `SERVEXA_BASE_URL` env fallback, else
 * empty) — spec A9.7 "pre-fill with the current effective value".
 */
export function initialBaseUrl(view) {
  return view?.effectiveBaseUrl ?? ''
}

/** Human summary of the stored-key state for the panel (never the raw key). */
export function describeKeyState(view) {
  return {
    configured: Boolean(view?.hasApiKey),
    masked: view?.apiKeyMasked ?? null,
    savedAt: view?.apiKeySavedAt ?? null,
  }
}

/** Whether a webhook signing secret is stored (never its value — spec A13.4). */
export function describeWebhookSecretState(view) {
  return { configured: Boolean(view?.hasWebhookSecret) }
}

/** Pre-fill value for the (non-secret) Public Issue Code input — the current
 *  stored value, editable like the base URL (spec A6a). */
export function initialPublicIssueCode(view) {
  return view?.publicIssueCode ?? ''
}

/** Pre-fill value for the (non-secret) Chat Widget URL input — the stored
 *  value, shown in full (spec A8b value 4, N26.2). Empty = Chat not set up. */
export function initialChatWidgetUrl(view) {
  return view?.chatWidgetUrl ?? ''
}

const LOOPBACK = new Set(['localhost', '127.0.0.1', '::1', '[::1]'])

/**
 * Client-side mirror of the server's Chat Widget URL rules (spec N26.2), for
 * an early, field-level message. The server (servexa.config.js
 * inspectChatWidgetUrl) stays authoritative — including the production-only
 * refusal of http://localhost, which the browser cannot know about.
 *
 * @returns {string|null} an error message, or null when acceptable
 */
export function chatWidgetUrlError(chatWidgetUrlInput, baseUrlInput) {
  const raw = String(chatWidgetUrlInput ?? '').trim()
  if (!raw) return null // empty = "Chat is not configured" — valid
  // Control characters are exactly what this refuses — the range is deliberate.
  // eslint-disable-next-line no-control-regex
  if (/[\s"'<>\\`\u0000-\u001f\u007f]/.test(raw)) {
    return 'The Chat Widget URL contains characters that are not allowed. Paste the exact URL Servexa gave you.'
  }
  // Must start "scheme://host": "https:host/x.js" would resolve as a path on
  // OUR origin in the browser.
  if (!/^https?:\/\/[^/]/i.test(raw)) {
    return 'Enter the complete Chat Widget URL Servexa gave you, starting with https://'
  }
  let url
  try {
    url = new URL(raw)
  } catch {
    return 'Enter the complete Chat Widget URL Servexa gave you, starting with https://'
  }
  if (url.username || url.password) {
    return 'The Chat Widget URL must not contain a user name or password.'
  }
  const localDev = url.protocol === 'http:' && LOOPBACK.has(url.hostname.toLowerCase())
  if (url.protocol !== 'https:' && !localDev) {
    return 'The Chat Widget URL must start with https://'
  }
  const base = String(baseUrlInput ?? '').trim().replace(/\/+$/, '')
  if (base) {
    if (raw.replace(/\/+$/, '') === base) {
      return 'This is the Base URL. The Chat Widget URL is a different value — the exact script URL Servexa gave you.'
    }
    try {
      const api = new URL(base)
      const host = (u) => u.hostname.toLowerCase().replace(/\.$/, '')
      if (api.protocol === url.protocol && api.port === url.port && host(api) === host(url)) {
        return "This points at the Servexa API host. Use the exact Chat Widget URL Servexa gave you — don't build it from the Base URL."
      }
    } catch {
      /* an invalid Base URL is reported on its own field */
    }
  }
  return null
}

/** Human summary of where the effective Base URL comes from (spec A9.7). */
export function describeBaseUrlSource(view) {
  switch (view?.baseUrlSource) {
    case 'database':
      return 'Using the saved database value.'
    case 'environment':
      return 'Using the SERVEXA_BASE_URL environment default. Saving here stores it in the database, which then takes precedence.'
    default:
      return 'No Base URL is configured on this deployment yet.'
  }
}

/**
 * Map a lifecycle state to what the panel shows and which action it offers
 * (spec A9.4/A9.5). `Request Sent` and `Connected` are DELIBERATELY never the
 * same state — `action: 'request' | 'retry'` is separate from `tone: success`.
 *
 * `action`:
 *   'request' → POST /connection/request  (from KEY_GENERATED / NOT_CONFIGURED)
 *   'retry'   → POST /connection/request  (after REJECTED — same endpoint)
 *   null      → no request button (a Refresh-status button is always shown)
 */
export function describeConnection(view) {
  const status = view?.status ?? 'NOT_CONFIGURED'
  const connected = view?.connected === true
  switch (status) {
    case 'ACTIVE':
      return {
        status,
        label: 'Connected',
        tone: 'success',
        hint: 'Approved and live — support submissions will reach Servexa.',
        action: null,
        connected,
      }
    case 'PENDING':
      return {
        status,
        label: 'Approval pending',
        tone: 'info',
        hint: 'Connection requested — waiting for a Servexa admin to approve it.',
        action: null,
        connected,
      }
    case 'KEY_GENERATED':
      return {
        status,
        label: 'Not connected',
        tone: 'warning',
        hint: 'The key is saved but no connection has been requested yet.',
        action: 'request',
        connected,
      }
    case 'REJECTED':
      return {
        status,
        label: 'Rejected',
        tone: 'danger',
        hint: 'A Servexa admin rejected the connection. Resolve it with them, then retry.',
        action: 'retry',
        connected,
      }
    case 'REVOKED':
      return {
        status,
        label: 'Revoked',
        tone: 'danger',
        hint: 'Servexa rejected this API key. Paste a current key and Save & Connect again.',
        action: null,
        connected,
      }
    case 'ERROR':
      return {
        status,
        label: 'Connection error',
        tone: 'danger',
        hint: view?.lastError || 'Could not reach Servexa. Check the Base URL and try again.',
        action: null,
        connected,
      }
    default:
      return {
        status: 'NOT_CONFIGURED',
        label: 'Not configured',
        tone: 'neutral',
        hint: 'Enter the integration key and Base URL, then Save & Connect.',
        action: null,
        connected: false,
      }
  }
}

/**
 * Build the `PUT /super-admin/servexa` request BODY for Save & Connect.
 *
 * The key is included ONLY when the admin typed a new one (`apiKeyDirty` and
 * non-empty) — omitting it leaves the stored key untouched (spec A9.2), which
 * is why an admin can edit the Base URL without re-pasting a key they cannot
 * read back. There is no "clear key" path (rotation is overwrite-only; revoke
 * is Servexa-side — spec A9.6).
 *
 * Returns a plain body object with no URL/query/params field — the key never
 * leaves this object.
 */
export function buildSavePayload({
  apiKeyInput,
  apiKeyDirty,
  baseUrlInput,
  publicIssueCodeInput,
  chatWidgetUrlInput,
  webhookSecretInput,
  webhookSecretDirty,
} = {}) {
  const body = {
    baseUrl: String(baseUrlInput ?? '').trim(),
    // Non-secret and editable like the base URL: always sent, so clearing it
    // (empty) removes it server-side (spec A6a).
    publicIssueCode: String(publicIssueCodeInput ?? '').trim(),
    // Same: always sent; empty clears it, meaning "Chat is not configured"
    // (spec N26.2). Exactly as typed (trimmed) — never rebuilt.
    chatWidgetUrl: String(chatWidgetUrlInput ?? '').trim(),
  }
  const key = String(apiKeyInput ?? '').trim()
  if (apiKeyDirty && key !== '') body.apiKey = key
  // A secret, like the API key: sent only when the admin typed a new one, so an
  // unchanged secret is left untouched (spec A13.4). There is no "clear" path.
  const secret = String(webhookSecretInput ?? '').trim()
  if (webhookSecretDirty && secret !== '') body.webhookSecret = secret
  return body
}

/**
 * Save & Connect input validation (spec A9.2). Field-level, so the page can
 * surface a message the admin can act on before the round-trip. The backend
 * DTO + `inspectBaseUrl` remain authoritative for hard failures.
 */
export function validateSaveInputs({
  apiKeyInput,
  apiKeyDirty,
  baseUrlInput,
  hasApiKey,
  chatWidgetUrlInput,
  effectiveBaseUrl,
  webhookSecretInput,
  webhookSecretDirty,
} = {}) {
  const errors = {}
  const key = String(apiKeyInput ?? '').trim()
  const url = String(baseUrlInput ?? '').trim()

  if (apiKeyDirty && key !== '') {
    if (!isValidApiKeyShape(key)) {
      errors.apiKey = 'Expected a Servexa key: sup_<prefix>_<64 hex characters>.'
    }
  } else if (!hasApiKey) {
    // First-time connect needs a key — none stored and none typed.
    errors.apiKey = 'Paste the Servexa integration key to connect.'
  }

  if (url !== '' && !looksLikeAbsoluteUrl(url)) {
    errors.baseUrl = 'Enter a full URL, e.g. https://api.servexa.com/api/v1.'
  }

  // Compared against the Base URL that will be in effect: the one typed, or
  // the current effective one (e.g. the environment default) when left empty.
  const chatError = chatWidgetUrlError(chatWidgetUrlInput, url || effectiveBaseUrl)
  if (chatError) errors.chatWidgetUrl = chatError

  // Webhook secret is optional; validate its shape only when the admin typed one.
  const secret = String(webhookSecretInput ?? '').trim()
  if (webhookSecretDirty && secret !== '' && !isValidWebhookSecretShape(secret)) {
    errors.webhookSecret = 'Expected a Servexa signing secret: whsec_<64 hex characters>.'
  }

  return { valid: Object.keys(errors).length === 0, errors }
}

/** Route-level access predicate — mirrors the router guard
 *  `ProtectedRoute allowRoles={['super_admin']}` (spec A9.2/A19/A20). */
export function canAccessServexaPanel(user) {
  return user?.role === 'super_admin'
}
